import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { CountryData } from 'react-phone-input-2';
import * as yup from 'yup';

import EyeIcon from '@/assets/svg/eye-icon';
import Button from '@/common/button';
import PhoneInput from '@/components/ui/phone-field';
import type { CreateUserTrialParams } from '@/context/types';
import { CountryCodeEnum } from '@/enums/country-code-enum';
import { useAuth } from '@/hooks/use-auth';
import { useCountryCode } from '@/hooks/use-country-code';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { useCountryNamesPhone } from '@/utils/country-names-phone';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { AuthLoadingOverlay, SsoProviders } from '../modals/sso';
import type { AlertStateOrEmpty } from '../ui/alert';
import { Alert } from '../ui/alert';
import CaptchaProvider from '../ui/captcha-provider';
import { AlreadyLoggedInNotice } from './already-logged-in-notice';
import SignInModal from './one-week/sign-in';

const defaultValues = {
  name: '',
  email: '',
  repeatEmail: '',
  phone: '',
  password: '',
  passwordRepeat: '',
  recaptcha: '',
  telegram: '',
};
interface FormData {
  name?: string;
  email: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
  telegram?: string | null;
}

interface CreateAccountProps {
  customTrialData?: {
    custom: number | null;
    trialPageId: number | null;
  };
  daysTrial?: number;
  isDisabledButton?: boolean;
  formId?: string;
  compact?: boolean;
  hideHeading?: boolean;
}

export default function CreateAccount({ customTrialData, daysTrial = 7, isDisabledButton = false, formId, compact = false, hideHeading = false }: CreateAccountProps) {
  const router = useRouter();
  const auth = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showMdalLogin, setModalLogin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const captchaResetRef = useRef<(() => void) | null>(null);
  const resetCaptcha = useCallback(() => captchaResetRef.current?.(), []);
  const { t } = useTranslation(['components/trial/create-account', 'common']);
  const disabledSignInButton = Boolean(!customTrialData);
  const [popUp, setPopUp] = useState<{
    show: boolean;
    message: string;
    state: AlertStateOrEmpty;
  }>({
    show: false,
    message: '',
    state: '',
  });
  const countryCodeUser = useCountryCode();

  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const handleSsoStart = useCallback(() => {
    setIsSsoAuthenticating(true);
  }, []);
  const handleSsoSuccess = useCallback(() => {
    setIsSsoAuthenticating(false);
  }, []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const schema = yup.object().shape({
    name: personNameSchema(t('common:login.mensage_error_empty_name'), t('common:login.mensage_error_invalid_name')),
    email: yup.string().email(t('common:login.mensage_error_valid_email')).required(t('common:login.mensage_error_empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('common:login.mensage_error_valid_email'))
      .required(t('common:login.mensage_error_empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('common:login.mensage_error_repeat_email')),
    password: yup.string().required(t('common:login.mensage_error_empty_password')).min(6, t('common:login.mensage_error_short_password')),
    passwordRepeat: yup
      .string()
      .required(t('common:login.mensage_error_empty_password'))
      .min(6, t('common:login.mensage_error_short_password'))
      .oneOf([yup.ref('password')], t('common:login.mensage_error_repeat_password')),
    recaptcha: yup.string().required(t('common:login.mensage_error_empty_captch')),
    telegram: yup.string().nullable().notRequired(),
  });

  const {
    register,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required' });
  }, [setValue, setError]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    resetCaptcha();
  }, [setValue, resetCaptcha]);

  const handleLogin = (data: FormData) => {
    auth.login({ login: data.email, password: data.password, recaptcha: data.recaptcha });
  };

  const onSubmit = async (data: FormData) => {
    const { email, password, passwordRepeat, recaptcha, name, telegram } = data;
    setIsLoading((prevState) => !prevState);

    const utmData = getUtmData();
    const lang = router.locale ?? 'en';

    try {
      const body: CreateUserTrialParams = {
        email,
        password,
        password_repeat: passwordRepeat,
        recaptcha,
        ref: null,
        name,
        lang,
        ...utmData,
      };

      if (telegram) {
        body.telegram = telegram;
      }

      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      if (customTrialData) {
        body.custom = customTrialData.custom;
        body.trialPageId = customTrialData.trialPageId;
      }

      await auth.createTrial(
        {
          ...body,
        },
        (error) => {
          // Reset captcha on error
          setValue('recaptcha', '');
          resetCaptcha();
          setPopUp({
            show: true,
            message: t(apiErrorMessageKey(error, { fallbackKey: 'common:login.mensage_error_create_user_generic', statusKeys: TRIAL_STATUS_KEYS })),
            state: 'error',
          });

          setIsLoading((prevState) => !prevState);
        },
        () => {
          handleLogin(data);

          sendGTMEvent({ event: 'createAccount' });
          reset();
          if (customTrialData) {
            sessionStorage.setItem('customTrialApproved', 'true');
            router.push('/thanks-for-trial-no-card');
          }
          setIsLoading((prevState) => !prevState);
        },
        false
      );
    } catch (error) {
      // Reset captcha on error
      setValue('recaptcha', '');
      resetCaptcha();
      setPopUp({
        show: true,
        message: t('common:login.mensage_error_create_user_generic'),
        state: 'error',
      });
      setIsLoading((prevState) => !prevState);
    }
  };

  const modalLogin = () => {
    setModalLogin(true);
  };

  const closeModalSingIn = () => {
    setModalLogin(false);
  };

  const country = useCountryNamesPhone();

  const wrapperPaddingClass = compact ? 'px-0' : 'px-5';
  const cardBgClass = compact ? '' : 'bg-[#0E1113]';
  const innerPaddingClass = compact ? 'px-4 py-6' : 'px-5 py-[61px] tablet:ps-12 xl:w-1/2';
  const inputPadding = compact ? 'px-4 py-3' : 'px-10 py-4';

  // A logged-in visitor on a custom-trial LP should not see a "create a new account" form.
  // AlreadyLoggedInNotice checks eligibility (SCRUM-1663) and offers to start the SAME trial from
  // the existing account when eligible. This never fires for the unavailable-trial state
  // (src/components/trial/trial-unavailable.tsx), which renders no form and no CreateAccount at all.
  if (customTrialData && auth.user) {
    return (
      <div className={`gtm-sign-up flex w-full items-center justify-center ${wrapperPaddingClass}`}>
        <div className={`relative w-full max-w-[1400px] overflow-hidden rounded-xl ${cardBgClass}`} id={formId}>
          <div className={`relative z-[1] ${innerPaddingClass}`}>
            <AlreadyLoggedInNotice trialConfigId={customTrialData.custom} trialPageId={customTrialData.trialPageId} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`gtm-sign-up flex w-full items-center justify-center ${wrapperPaddingClass}`}>
      <div className={`relative w-full max-w-[1400px] overflow-hidden rounded-xl ${cardBgClass}`} id={formId}>
        <div className={`relative z-[1] ${innerPaddingClass}`}>
          <AuthLoadingOverlay isActive={isSsoAuthenticating} />
          {!hideHeading && (
            <div className='mb-5'>
              <h3 className='mb-4 text-2xl font-bold leading-[110%] text-white'>{customTrialData ? t('trial.create_your_account_custom_trial', { count: daysTrial }) : t('trial.create_your_account')}</h3>
              {!customTrialData && (
                <p className='text-sm font-medium leading-[140%] text-[#879BAB]'>
                  <Trans components={{ br: <br /> }} i18nKey={t('trial.create_your_account_description', { count: daysTrial })} values={{ count: daysTrial }} />
                </p>
              )}
            </div>
          )}
          {popUp.show && <Alert message={popUp.message} state={popUp.state} className='my-2.5' onClose={() => setPopUp((prev) => ({ ...prev, show: false }))} />}
          <div className='mb-5'>
            <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={customTrialData ? { custom: customTrialData.custom, trialPageId: customTrialData.trialPageId } : undefined} />
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-2.5' autoComplete='off'>
            <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.name?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} ${inputPadding}`}>
              <input data-lpignore='true' placeholder={t('trial.input_name')} type='text' {...register('name')} dir='auto' autoComplete='name' className={classNames('w-full bg-transparent text-sm text-[#D6E4EF] outline-0 placeholder:font-bold placeholder:uppercase focus:outline-0')} />
              {errors.name && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.name?.message}</span>}
            </div>
            <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.email?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} ${inputPadding}`}>
              <input data-lpignore='true' placeholder={t('trial.input_email')} type='text' {...register('email')} className={classNames('w-full bg-transparent text-sm text-[#D6E4EF] outline-0 placeholder:font-bold placeholder:uppercase focus:outline-0')} />
              {errors.email && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.email?.message}</span>}
            </div>
            <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.repeatEmail?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} ${inputPadding}`}>
              <input data-lpignore='true' placeholder={t('trial.input_repeat_email')} type='text' {...register('repeatEmail')} className={classNames('w-full bg-transparent text-sm text-[#D6E4EF] outline-0 placeholder:font-bold placeholder:uppercase focus:outline-0')} />
              {errors.repeatEmail && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.repeatEmail?.message}</span>}
            </div>
            {countryCodeUser === CountryCodeEnum.RUSSIAN && (
              <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.telegram?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} justify-start ${inputPadding}`}>
                <div className='flex w-full items-center justify-start gap-12'>
                  <PhoneInput
                    {...register('telegram')}
                    localization={country}
                    enableSearch
                    searchNotFound={`${t('common:login.search_country_not_found')}`}
                    searchPlaceholder={`${t('common:login.search')}`}
                    i18nIsDynamicList
                    searchStyle={{
                      backgroundColor: '#1E293B',
                      color: '#fff',
                    }}
                    containerStyle={{
                      width: '0',
                      color: '#D6E4EF',
                    }}
                    inputStyle={{
                      backgroundColor: 'transparent',
                      color: '#D6E4EF',
                      border: 'none',
                      fontFamily: bai_jamjuree.style.fontFamily,
                    }}
                    buttonStyle={{
                      backgroundColor: 'transparent',
                      borderRadius: '4px',
                    }}
                    dropdownStyle={{
                      backgroundColor: '#1E293B',
                      color: '#D6E4EF',
                    }}
                    inputClass='custom-phone-input'
                    placeholder={t('common:login.placeholder.telegram')}
                    onChange={(value: string, _data: CountryData) => {
                      setValue('telegram', value, { shouldValidate: true, shouldDirty: true });
                    }}
                  />
                </div>
                {errors.telegram && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.telegram?.message}</span>}
              </div>
            )}
            <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.password?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} ${inputPadding}`}>
              <div className='flex w-full gap-1'>
                <input data-lpignore='true' placeholder={t('trial.input_password')} type={showPassword ? 'text' : 'password'} {...register('password')} className={classNames('w-full bg-transparent text-sm text-[#D6E4EF] outline-0 placeholder:font-bold placeholder:uppercase focus:outline-0')} />
                <span className='cursor-pointer text-[#515F6C] transition-all hover:opacity-70' onClick={() => setShowPassword(!showPassword)}>
                  <EyeIcon />
                </span>
              </div>
              {errors.password && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.password?.message}</span>}
            </div>
            <div className={`flex w-full items-center gap-4 rounded-xl border ${errors?.passwordRepeat?.message ? 'border-[#FD0C34]' : 'border-[#879BAB]'} ${inputPadding}`}>
              <div className='flex w-full gap-1'>
                <input data-lpignore='true' placeholder={t('trial.input_confirm_password')} type={showPassword ? 'text' : 'password'} {...register('passwordRepeat')} className={classNames('w-full bg-transparent text-sm text-[#D6E4EF] outline-0 placeholder:font-bold placeholder:uppercase focus:outline-0')} />
                <span className='cursor-pointer text-[#515F6C] transition-all hover:opacity-70' onClick={() => setShowPassword(!showPassword)}>
                  <EyeIcon />
                </span>
              </div>
              {errors.passwordRepeat && <span className='ms-1 flex w-1/2 items-center text-xs font-medium tracking-wide text-red-500'>{errors?.passwordRepeat?.message}</span>}
            </div>
            <div className='mt-2 flex flex-col place-content-center gap-5'>
              <div className='text-center'>
                <div className='dispaly:inline-block'>
                  <CaptchaProvider resetRef={captchaResetRef} onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} />
                </div>
              </div>
              {errors.recaptcha && <span className='ms-1 flex items-center text-xs font-medium tracking-wide text-red-500'>{errors.recaptcha?.message}</span>}
            </div>
            <div className='flex flex-col gap-4'>
              <Button type='submit' id='click_obtenha_sete_dias_gratuitos' className='!bg-[#AEF55C] py-3 text-black transition-opacity hover:opacity-80' fullWidth loading={isLoading} disabled={isDisabledButton}>
                {isLoading ? t('trial.buttons.submitting') : t('trial.buttons.sign_up', { count: daysTrial })}
              </Button>

              {disabledSignInButton && (
                <Button type='button' className={`w-full rounded-xl bg-[#363D43] py-4 text-center transition-opacity hover:opacity-80 mobile:px-10 ${isLoading ? '!cursor-not-allowed !opacity-80' : ''}`} variant='gray' fullWidth disabled={isLoading} onClick={() => modalLogin()}>
                  {t('trial.buttons.sign_in')}
                </Button>
              )}
            </div>
          </form>
          {showMdalLogin && <SignInModal closeModalSingIn={closeModalSingIn} />}
        </div>
        {!compact && (
          <div className='absolute -bottom-5 end-0 hidden h-full w-1/2 items-end justify-end laptop:flex'>
            <Image className='object-fit size-full h-auto select-none' src='/assets/images/trial/seven-days/background.png' alt='Seed' width={1344} height={1406} />
          </div>
        )}
      </div>
    </div>
  );
}
